import React from 'react';
import { FiPlus, FiTrash2, FiDownload } from 'react-icons/fi';
import {
  newCertificationId,
  newEducationId,
  newExperienceId,
  newLanguageId,
  newLinkId,
  newProjectId,
  profileSectionsFromResumeClient,
} from '../utils/resumeExtract';
import './CandidateProfileResumeSections.css';

const LANGUAGE_LEVELS = ['Native', 'Fluent', 'Professional', 'Conversational', 'Basic'];

/**
 * Full resume-like sections for candidate profile (edit + view).
 */
export default function CandidateProfileResumeSections({
  profile,
  editing = false,
  onChange,
  resumes = [],
  onImportFromResume,
}) {
  const p = profile || {};
  const skills = Array.isArray(p.skills) ? p.skills : [];
  const experience = Array.isArray(p.work_experience) ? p.work_experience : [];
  const education = Array.isArray(p.education_history) ? p.education_history : [];
  const certifications = Array.isArray(p.certifications) ? p.certifications : [];
  const languages = Array.isArray(p.languages) ? p.languages : [];
  const projects = Array.isArray(p.projects) ? p.projects : [];
  const links = Array.isArray(p.links) ? p.links : [];

  const patch = (partial) => onChange?.({ ...p, ...partial });

  const importResume = (resume) => {
    const sections = profileSectionsFromResumeClient(resume);
    const next = { ...p, ...sections };
    if (sections.years_of_experience === undefined) delete next.years_of_experience;
    onChange?.(next);
    onImportFromResume?.(sections);
  };

  if (!editing) {
    const hasResumeBody =
      skills.length ||
      experience.length ||
      education.length ||
      certifications.length ||
      languages.length ||
      projects.length ||
      links.length;

    if (!hasResumeBody) {
      return (
        <p className="form-hint profile-resume-empty">
          No resume details on this profile yet (skills, experience, education, etc.).
        </p>
      );
    }

    return (
      <div className="profile-resume-view">
        {skills.length > 0 && (
          <section className="profile-resume-block">
            <h4>Skills</h4>
            <div className="profile-skill-tags">
              {skills.map((skill) => (
                <span key={skill} className="profile-skill-tag">
                  {skill}
                </span>
              ))}
            </div>
          </section>
        )}

        {experience.length > 0 && (
          <section className="profile-resume-block">
            <h4>Work experience</h4>
            {experience.map((exp, idx) => (
              <div key={exp.id || idx} className="profile-resume-entry">
                <div className="profile-resume-entry-head">
                  <strong>{exp.title || 'Role'}</strong>
                  {exp.company ? <span> · {exp.company}</span> : null}
                </div>
                {exp.dates ? <div className="form-hint">{exp.dates}</div> : null}
                {(exp.bullets || []).filter(Boolean).length > 0 && (
                  <ul>
                    {exp.bullets.filter(Boolean).map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </section>
        )}

        {education.length > 0 && (
          <section className="profile-resume-block">
            <h4>Education</h4>
            {education.map((edu, idx) => (
              <div key={edu.id || idx} className="profile-resume-entry">
                <strong>{edu.degree || 'Degree'}</strong>
                {edu.school ? <span> · {edu.school}</span> : null}
                {edu.dates ? <div className="form-hint">{edu.dates}</div> : null}
              </div>
            ))}
          </section>
        )}

        {certifications.length > 0 && (
          <section className="profile-resume-block">
            <h4>Certifications</h4>
            {certifications.map((c, idx) => (
              <div key={c.id || idx} className="profile-resume-entry">
                <strong>{c.name || 'Certification'}</strong>
                {c.issuer ? <span> · {c.issuer}</span> : null}
                {c.date ? <div className="form-hint">{c.date}</div> : null}
              </div>
            ))}
          </section>
        )}

        {languages.length > 0 && (
          <section className="profile-resume-block">
            <h4>Languages</h4>
            <ul className="profile-resume-plain-list">
              {languages.map((lang, idx) => (
                <li key={lang.id || idx}>
                  {lang.language || 'Language'}
                  {lang.proficiency ? ` — ${lang.proficiency}` : ''}
                </li>
              ))}
            </ul>
          </section>
        )}

        {projects.length > 0 && (
          <section className="profile-resume-block">
            <h4>Projects</h4>
            {projects.map((proj, idx) => (
              <div key={proj.id || idx} className="profile-resume-entry">
                <strong>
                  {proj.url ? (
                    <a href={proj.url} target="_blank" rel="noopener noreferrer">
                      {proj.name || proj.url}
                    </a>
                  ) : (
                    proj.name || 'Project'
                  )}
                </strong>
                {proj.description ? <p>{proj.description}</p> : null}
                {(proj.technologies || []).length > 0 && (
                  <div className="profile-skill-tags">
                    {proj.technologies.map((t) => (
                      <span key={t} className="profile-skill-tag">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </section>
        )}

        {links.length > 0 && (
          <section className="profile-resume-block">
            <h4>Additional links</h4>
            <ul className="profile-resume-plain-list">
              {links.map((link, idx) => (
                <li key={link.id || idx}>
                  {link.url ? (
                    <a href={link.url} target="_blank" rel="noopener noreferrer">
                      {link.label || link.url}
                    </a>
                  ) : (
                    link.label || '—'
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    );
  }

  return (
    <div className="profile-resume-edit">
      {resumes.length > 0 && (
        <div className="profile-resume-import">
          <label htmlFor="profile-import-resume">Import from resume</label>
          <div className="profile-resume-import-row">
            <select
              id="profile-import-resume"
              defaultValue=""
              onChange={(e) => {
                const resume = resumes.find((r) => String(r.id) === e.target.value);
                if (resume) importResume(resume);
                e.target.value = '';
              }}
            >
              <option value="">— Choose a resume to copy sections —</option>
              {resumes.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.file_name || `Resume #${r.id}`}
                </option>
              ))}
            </select>
            <span className="form-hint">
              <FiDownload aria-hidden /> Copies skills, experience, education, and related sections
              into this profile.
            </span>
          </div>
        </div>
      )}

      <section className="profile-resume-block">
        <h4>Skills</h4>
        <input
          type="text"
          value={skills.join(', ')}
          onChange={(e) =>
            patch({
              skills: e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          placeholder="React, Node.js, SQL, …"
        />
        <small className="form-hint">Comma-separated</small>
      </section>

      <section className="profile-resume-block">
        <div className="profile-resume-block-head">
          <h4>Work experience</h4>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() =>
              patch({
                work_experience: [
                  ...experience,
                  { id: newExperienceId(), title: '', company: '', dates: '', bullets: [''] },
                ],
              })
            }
          >
            <FiPlus aria-hidden /> Add role
          </button>
        </div>
        {experience.length === 0 && <p className="form-hint">No roles yet.</p>}
        {experience.map((exp, idx) => (
          <div key={exp.id || idx} className="profile-resume-card">
            <div className="form-row">
              <div className="form-group">
                <label>Title</label>
                <input
                  value={exp.title || ''}
                  onChange={(e) => {
                    const next = [...experience];
                    next[idx] = { ...next[idx], title: e.target.value };
                    patch({ work_experience: next });
                  }}
                />
              </div>
              <div className="form-group">
                <label>Company</label>
                <input
                  value={exp.company || ''}
                  onChange={(e) => {
                    const next = [...experience];
                    next[idx] = { ...next[idx], company: e.target.value };
                    patch({ work_experience: next });
                  }}
                />
              </div>
              <div className="form-group">
                <label>Dates</label>
                <input
                  value={exp.dates || ''}
                  onChange={(e) => {
                    const next = [...experience];
                    next[idx] = { ...next[idx], dates: e.target.value };
                    patch({ work_experience: next });
                  }}
                  placeholder="2020 – Present"
                />
              </div>
            </div>
            <label>Highlights</label>
            {(exp.bullets || ['']).map((bullet, bIdx) => (
              <div key={bIdx} className="profile-resume-bullet-row">
                <input
                  value={bullet}
                  onChange={(e) => {
                    const next = [...experience];
                    const bullets = [...(next[idx].bullets || [])];
                    bullets[bIdx] = e.target.value;
                    next[idx] = { ...next[idx], bullets };
                    patch({ work_experience: next });
                  }}
                  placeholder="Achievement or responsibility"
                />
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    const next = [...experience];
                    const bullets = (next[idx].bullets || []).filter((_, i) => i !== bIdx);
                    next[idx] = { ...next[idx], bullets: bullets.length ? bullets : [''] };
                    patch({ work_experience: next });
                  }}
                  aria-label="Remove bullet"
                >
                  <FiTrash2 aria-hidden />
                </button>
              </div>
            ))}
            <div className="profile-resume-card-actions">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  const next = [...experience];
                  next[idx] = {
                    ...next[idx],
                    bullets: [...(next[idx].bullets || []), ''],
                  };
                  patch({ work_experience: next });
                }}
              >
                <FiPlus aria-hidden /> Bullet
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() =>
                  patch({ work_experience: experience.filter((_, i) => i !== idx) })
                }
              >
                <FiTrash2 aria-hidden /> Remove role
              </button>
            </div>
          </div>
        ))}
      </section>

      <section className="profile-resume-block">
        <div className="profile-resume-block-head">
          <h4>Education</h4>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() =>
              patch({
                education_history: [
                  ...education,
                  { id: newEducationId(), degree: '', school: '', dates: '' },
                ],
              })
            }
          >
            <FiPlus aria-hidden /> Add education
          </button>
        </div>
        {education.map((edu, idx) => (
          <div key={edu.id || idx} className="profile-resume-card">
            <div className="form-row">
              <div className="form-group">
                <label>Degree</label>
                <input
                  value={edu.degree || ''}
                  onChange={(e) => {
                    const next = [...education];
                    next[idx] = { ...next[idx], degree: e.target.value };
                    patch({ education_history: next });
                  }}
                />
              </div>
              <div className="form-group">
                <label>School</label>
                <input
                  value={edu.school || ''}
                  onChange={(e) => {
                    const next = [...education];
                    next[idx] = { ...next[idx], school: e.target.value };
                    patch({ education_history: next });
                  }}
                />
              </div>
              <div className="form-group">
                <label>Dates</label>
                <input
                  value={edu.dates || ''}
                  onChange={(e) => {
                    const next = [...education];
                    next[idx] = { ...next[idx], dates: e.target.value };
                    patch({ education_history: next });
                  }}
                />
              </div>
            </div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() =>
                patch({ education_history: education.filter((_, i) => i !== idx) })
              }
            >
              <FiTrash2 aria-hidden /> Remove
            </button>
          </div>
        ))}
      </section>

      <section className="profile-resume-block">
        <div className="profile-resume-block-head">
          <h4>Certifications</h4>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() =>
              patch({
                certifications: [
                  ...certifications,
                  { id: newCertificationId(), name: '', issuer: '', date: '' },
                ],
              })
            }
          >
            <FiPlus aria-hidden /> Add certification
          </button>
        </div>
        {certifications.map((c, idx) => (
          <div key={c.id || idx} className="profile-resume-card">
            <div className="form-row">
              <div className="form-group">
                <label>Name</label>
                <input
                  value={c.name || ''}
                  onChange={(e) => {
                    const next = [...certifications];
                    next[idx] = { ...next[idx], name: e.target.value };
                    patch({ certifications: next });
                  }}
                />
              </div>
              <div className="form-group">
                <label>Issuer</label>
                <input
                  value={c.issuer || ''}
                  onChange={(e) => {
                    const next = [...certifications];
                    next[idx] = { ...next[idx], issuer: e.target.value };
                    patch({ certifications: next });
                  }}
                />
              </div>
              <div className="form-group">
                <label>Date</label>
                <input
                  value={c.date || ''}
                  onChange={(e) => {
                    const next = [...certifications];
                    next[idx] = { ...next[idx], date: e.target.value };
                    patch({ certifications: next });
                  }}
                />
              </div>
            </div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() =>
                patch({ certifications: certifications.filter((_, i) => i !== idx) })
              }
            >
              <FiTrash2 aria-hidden /> Remove
            </button>
          </div>
        ))}
      </section>

      <section className="profile-resume-block">
        <div className="profile-resume-block-head">
          <h4>Languages</h4>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() =>
              patch({
                languages: [
                  ...languages,
                  { id: newLanguageId(), language: '', proficiency: 'Professional' },
                ],
              })
            }
          >
            <FiPlus aria-hidden /> Add language
          </button>
        </div>
        {languages.map((lang, idx) => (
          <div key={lang.id || idx} className="profile-resume-card form-row">
            <div className="form-group">
              <label>Language</label>
              <input
                value={lang.language || ''}
                onChange={(e) => {
                  const next = [...languages];
                  next[idx] = { ...next[idx], language: e.target.value };
                  patch({ languages: next });
                }}
              />
            </div>
            <div className="form-group">
              <label>Proficiency</label>
              <select
                value={lang.proficiency || 'Professional'}
                onChange={(e) => {
                  const next = [...languages];
                  next[idx] = { ...next[idx], proficiency: e.target.value };
                  patch({ languages: next });
                }}
              >
                {LANGUAGE_LEVELS.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => patch({ languages: languages.filter((_, i) => i !== idx) })}
              >
                <FiTrash2 aria-hidden /> Remove
              </button>
            </div>
          </div>
        ))}
      </section>

      <section className="profile-resume-block">
        <div className="profile-resume-block-head">
          <h4>Projects</h4>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() =>
              patch({
                projects: [
                  ...projects,
                  {
                    id: newProjectId(),
                    name: '',
                    url: '',
                    description: '',
                    technologies: [],
                  },
                ],
              })
            }
          >
            <FiPlus aria-hidden /> Add project
          </button>
        </div>
        {projects.map((proj, idx) => (
          <div key={proj.id || idx} className="profile-resume-card">
            <div className="form-row">
              <div className="form-group">
                <label>Name</label>
                <input
                  value={proj.name || ''}
                  onChange={(e) => {
                    const next = [...projects];
                    next[idx] = { ...next[idx], name: e.target.value };
                    patch({ projects: next });
                  }}
                />
              </div>
              <div className="form-group">
                <label>URL</label>
                <input
                  value={proj.url || ''}
                  onChange={(e) => {
                    const next = [...projects];
                    next[idx] = { ...next[idx], url: e.target.value };
                    patch({ projects: next });
                  }}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                rows={2}
                value={proj.description || ''}
                onChange={(e) => {
                  const next = [...projects];
                  next[idx] = { ...next[idx], description: e.target.value };
                  patch({ projects: next });
                }}
              />
            </div>
            <div className="form-group">
              <label>Technologies (comma-separated)</label>
              <input
                value={(proj.technologies || []).join(', ')}
                onChange={(e) => {
                  const next = [...projects];
                  next[idx] = {
                    ...next[idx],
                    technologies: e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  };
                  patch({ projects: next });
                }}
              />
            </div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => patch({ projects: projects.filter((_, i) => i !== idx) })}
            >
              <FiTrash2 aria-hidden /> Remove
            </button>
          </div>
        ))}
      </section>

      <section className="profile-resume-block">
        <div className="profile-resume-block-head">
          <h4>Additional links</h4>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() =>
              patch({
                links: [...links, { id: newLinkId(), label: '', url: '' }],
              })
            }
          >
            <FiPlus aria-hidden /> Add link
          </button>
        </div>
        {links.map((link, idx) => (
          <div key={link.id || idx} className="profile-resume-card form-row">
            <div className="form-group">
              <label>Label</label>
              <input
                value={link.label || ''}
                onChange={(e) => {
                  const next = [...links];
                  next[idx] = { ...next[idx], label: e.target.value };
                  patch({ links: next });
                }}
                placeholder="Personal site"
              />
            </div>
            <div className="form-group">
              <label>URL</label>
              <input
                value={link.url || ''}
                onChange={(e) => {
                  const next = [...links];
                  next[idx] = { ...next[idx], url: e.target.value };
                  patch({ links: next });
                }}
              />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => patch({ links: links.filter((_, i) => i !== idx) })}
              >
                <FiTrash2 aria-hidden /> Remove
              </button>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}

export function profileHasResumeSections(profile) {
  if (!profile) return false;
  return [
    'skills',
    'work_experience',
    'education_history',
    'certifications',
    'languages',
    'projects',
    'links',
  ].some((key) => Array.isArray(profile[key]) && profile[key].length > 0);
}
